import { identity, sortBy } from 'lodash'

import { batch } from './undo'

import {
  ADD_CHARACTER,
  ADD_CHARACTER_WITH_TEMPLATE,
  ATTACH_BOOK_TO_CHARACTER,
  ATTACH_TAG_TO_CHARACTER,
  DELETE_CHARACTER,
  LOAD_CHARACTERS,
  LOAD_CHARACTER,
  BATCH_LOAD_CHARACTER,
  REMOVE_CHARACTER,
  ADD_TEMPLATE_TO_CHARACTER,
  REMOVE_TEMPLATE_FROM_CHARACTER,
  REMOVE_BOOK_FROM_CHARACTER,
  REMOVE_TAG_FROM_CHARACTER,
  EDIT_CHARACTER_TEMPLATE_ATTRIBUTE,
  DUPLICATE_CHARACTER,
  CREATE_CHARACTER_ATTRIBUTE,
  EDIT_CHARACTER_ATTRIBUTE_VALUE,
  EDIT_CHARACTER_SHORT_DESCRIPTION,
  EDIT_CHARACTER_DESCRIPTION,
  EDIT_CHARACTER_CATEGORY,
  EDIT_CHARACTER_NAME,
  EDIT_CHARACTER_IMAGE,
  DELETE_CHARACTER_LEGACY_CUSTOM_ATTRIBUTE,
  SELECT_CHARACTER_ATTRIBUTE_BOOK_TAB,
  REORDER_CHARACTER_TEMPLATES,
  REORDER_CHARACTER_MANUALLY,
} from '../constants/ActionTypes'
import selectors from '../selectors'
import { character } from '../store/initialState'
import { escapeBraces } from './customAttributes'
import { nextId } from '../store/newIds'

/**
 * @typedef {import('../types/plottr').Slate} Slate
 * @typedef {import('../types/plottr').AttributeType} AttributeType
 * @typedef {import('../types/plottr').AttributeValue} AttributeValue
 * @typedef {import('../index').TextAttributeData} TextAttributeData
 * @typedef {import('../index').Character} Character
 * @typedef {import('../index').DOMSelection} DOMSelection
 * @typedef {import('../index').SlateSelection} SlateSelection
 * @typedef {import('../index').Dispatch} Dispatch
 * @typedef {import('../index').GetState} GetState
 * @typedef {import('../types/system').CharacterTemplate} CharacterTemplate
 * @typedef {import('../types/system').CharacterCustomTemplate} CharacterCustomTemplate
 */

/**
 * @typedef {{ type: 'CREATE_CHARACTER_ATTRIBUTE', attribute: { type: AttributeType, name: String } | { type: AttributeType, name: String, value: String | Slate }, fromLegacyAttribute: boolean, nextAttributeId: number, bookId: 'series' | number, currentBookId: 'all' | number }} CreateCharacterAttributeAction
 * @typedef {{ type: 'REORDER_CHARACTER_MANUALLY', characterIdsInOrder: Array<number>, characterId: number, newPosition: number, newCategoryId: String | Null, direction: 'up' | 'down', categoryAttributeId: number, positionAttributeId: number, bookId: number | 'series' }} ReorderCharacterManuallyAction
 * @typedef {{ type: 'ATTACH_TAG_TO_CHARACTER', id: number, tagId: number, attributeId: number }} AttachTagToCharacter
 * @typedef {{ type: 'REMOVE_TAG_FROM_CHARACTER', id: number, tagId: number, attributeId: number }} RemoveTagFromCharacter
 * @typedef {{ type: 'EDIT_CHARACTER_SHORT_DESCRIPTION', characterId: number, value: String, selection: DOMSelection, attributeId: number, bookId: 'series' | number, currentBookId: 'all' | number }} EditCharacterAttributeAction
 * @typedef {{ type: 'ADD_CHARACTER', name: String, description: String, notes: TextAttributeData, nextCharacterId: number, currentBookId: number | 'all' }} AddCharacterAction
 * @typedef {{ type: 'EDIT_CHARACTER_ATTRIBUTE_VALUE', characterId: number, attributeId: number | String, value: TextAttributeData, selection: DOMSelection | SlateSelection | Null, bookId: 'series' | number, currentBookId: 'all' | number }} EditCharacterAttributeValueAction
 * @typedef {Omit<EditCharacterAttributeValueAction, 'type'> & { type: 'EDIT_CHARACTER_CATEGORY'}} EditCharacterCategoryAction
 * @typedef {Omit<EditCharacterAttributeValueAction, 'type'> & { type: 'EDIT_CHARACTER_DESCRIPTION'}} EditCharacterDescriptionAction
 * @typedef {Omit<EditCharacterAttributeValueAction, 'type'> & { type: 'EDIT_CHARACTER_SHORT_DESCRIPTION'}} EditCharacterShortDescriptionAction
 * @typedef {{ type: 'ADD_CHARACTER_WITH_TEMPLATE', name: String, description: String, notes: TextAttributeData, templateData: CharacterTemplate | CharacterCustomTemplate, nextCharacterId: number }} AddCharacterWithTemplateAction
 */

/**
 * @typedef {ReturnType<typeof editCharacterName> | ReturnType<typeof editCharacterImage> | ReturnType<typeof _editCharacterTemplateAttribute> | ReturnType<typeof addTemplateToCharacter> | ReturnType<typeof deleteCharacter> | ReturnType<typeof _attachBookToCharacter> | ReturnType<typeof _removeBookFromCharacter> | ReturnType<typeof _selectCharacterAttributeBookTab> | ReturnType<typeof removeTemplateFromCharacter> | ReturnType<typeof load> | ReturnType<typeof loadSingle> | ReturnType<typeof batchLoad> | ReturnType<typeof removeSingle> | ReturnType<typeof duplicateCharacter> | ReturnType<typeof _deleteCharacterLegacyCustomAttribute> | ReturnType<typeof reorderCharacterTemplateAttribute> | CreateCharacterAttributeAction | ReorderCharacterManuallyAction | RemoveTagFromCharacter | AttachTagToCharacter | EditCharacterAttributeAction | EditCharacterDescriptionAction | EditCharacterCategoryAction | AddCharacterAction | EditCharacterAttributeValueAction | EditCharacterShortDescriptionAction | AddCharacterWithTemplateAction} CharactersActions
 */

const {
  characterAttributesForCurrentBookSelector,
  selectedCharacterAttributeTabSelector,
  legacyCustomCharacterAttributeByName,
  characterAttributesForBookSelector,
  allBookIdsSelector,
  allDisplayedCharactersForCurrentBookSelector,
  visibleSortedCharactersByCategorySelector,
} = selectors(identity)

/**
 * @param {String | Null} name
 * @return {{ type: 'ADD_CHARACTER', name: String, description: String, notes: TextAttributeData }}
 */
export function addCharacter(name) {
  return {
    type: ADD_CHARACTER,
    name: name || character.name,
    description: character.description,
    notes: character.notes,
  }
}

/**
 * @param {String} name
 * @param {String} description
 * @param {Character} rest
 * @return {{ type: 'ADD_CHARACTER', name: String, description: TextAttributeData } & Character}
 */
export function addCharacterWithValues(name, description, rest) {
  return {
    type: ADD_CHARACTER,
    ...rest,
    name: name || character.name,
    description: description ?? character.description,
  }
}

/**
 * @param {String | Null} name
 * @param {CharacterTemplate | CharacterCustomTemplate} templateData
 * @return {{ type: 'ADD_CHARACTER_WITH_TEMPLATE', name: String, description: String, notes: TextAttributeData, templateData: CharacterTemplate | CharacterCustomTemplate }}
 */
export function addCharacterWithTemplate(name, templateData) {
  return {
    type: ADD_CHARACTER_WITH_TEMPLATE,
    name: name || character.name,
    description: character.description,
    notes: character.notes,
    templateData,
  }
}

/**
 * @param {number} id
 * @param {String} name
 * @param {DOMSelection | Null} selection
 * @return {{ type: 'EDIT_CHARACTER_NAME', id: number, name: String, selection: DOMSelection | Null }}
 */
export function editCharacterName(id, name, selection) {
  return {
    type: EDIT_CHARACTER_NAME,
    id,
    name,
    selection,
  }
}

/**
 * @param {number} id
 * @param {String | Null} imageId
 * @return {{ type: 'EDIT_CHARACTER_IMAGE', id: number, imageId: String | Null}}
 */
export function editCharacterImage(id, imageId) {
  return {
    type: EDIT_CHARACTER_IMAGE,
    id,
    imageId,
  }
}

/**
 * @param {number} id
 * @param {String} templateId
 * @param {String} name
 * @param {TextAttributeData} value
 * @param {'all' | number} bookId
 * @param {DOMSelection | SlateSelection} selection
 * @return {{ type: 'EDIT_CHARACTER_TEMPLATE_ATTRIBUTE', id: number, templateId: String, name: String, value: TextAttributeData, bookId: 'all' | number, selection: DOMSelection | SlateSelection }}
 */
export const _editCharacterTemplateAttribute = (id, templateId, name, value, bookId, selection) => {
  return {
    type: EDIT_CHARACTER_TEMPLATE_ATTRIBUTE,
    id,
    templateId,
    name,
    value,
    bookId,
    selection,
  }
}

/**
 * @param {number} id
 * @param {String} templateId
 * @param {String} name
 * @param {TextAttributeData} value
 * @param {DOMSelection | SlateSelection} selection
 * @return {function(Dispatch, GetState): Void}
 */
export const editCharacterTemplateAttribute =
  (id, templateId, name, value, selection) => (dispatch, getState) => {
    const state = getState()
    const bookId = selectedCharacterAttributeTabSelector(state)

    dispatch(_editCharacterTemplateAttribute(id, templateId, name, value, bookId, selection))
  }

/**
 * @param {number} id
 * @param {CharacterTemplate | CharacterCustomTemplate} templateData
 * @return {{ type: 'ADD_TEMPLATE_TO_CHARACTER', id: number, templateData: CharacterTemplate | CharacterCustomTemplate }}
 */
export function addTemplateToCharacter(id, templateData) {
  return { type: ADD_TEMPLATE_TO_CHARACTER, id, templateData }
}

/**
 * @param {number} id
 * @return {{ type: 'DELETE_CHARACTER', id: number }}
 */
export function deleteCharacter(id) {
  return { type: DELETE_CHARACTER, id }
}

/**
 * @param {number} id
 * @param {number} tagId
 * @return {{ type: 'ATTACH_TAG_TO_CHARACTER', id: number, tagId: number }}
 */
export function addTag(id, tagId) {
  return { type: ATTACH_TAG_TO_CHARACTER, id, tagId }
}

/**
 * @param {number} id
 * @param {'series' | number} bookId
 * @return {{ type: ATTACH_BOOK_TO_CHARACTER, id: number, bookId: 'series' | number }}
 */
export const _attachBookToCharacter = (id, bookId) => {
  return { type: ATTACH_BOOK_TO_CHARACTER, id, bookId }
}

/**
 * @param {number} id
 * @param {number | 'series'} bookId
 * @return {function(Dispatch, GetState): Void}
 */
export const addBook = (id, bookId) => (dispatch, getState) => {
  const state = getState()
  const bookIds = allBookIdsSelector(state)

  if (bookId === 'series' || bookIds.indexOf(bookId) > -1) {
    dispatch(_attachBookToCharacter(id, bookId))
  }
  // TODO: error message?
}

/**
 * @param {number} id
 * @param {number} tagId
 * @return {{ type: 'REMOVE_TAG_FROM_CHARACTER', id: number, tagId: number }}
 */
export function removeTag(id, tagId) {
  return { type: REMOVE_TAG_FROM_CHARACTER, id, tagId }
}

/**
 * @param {number} id
 * @param {'series' | number} bookId
 * @return {{ type: 'REMOVE_BOOK_FROM_CHARACTER', id: number, bookId: number | 'series' }}
 */
export const _removeBookFromCharacter = (id, bookId) => {
  return { type: REMOVE_BOOK_FROM_CHARACTER, id, bookId }
}

/**
 * @return {{ type: 'SELECT_CHARACTER_ATTRIBUTE_BOOK_TAB', bookId: 'all' }}
 */
export const _selectCharacterAttributeBookTab = () => {
  return { type: SELECT_CHARACTER_ATTRIBUTE_BOOK_TAB, bookId: 'all' }
}

/**
 * @param {number} id
 * @param {number | 'series'} bookId
 * @return {function(Dispatch, GetState): Void}
 */
export const removeBook = (id, bookId) => (dispatch, getState) => {
  const state = getState()
  const allCharactersByBook = allDisplayedCharactersForCurrentBookSelector(state, null, null)

  batch('Remove Book from Character', () => {
    dispatch(_removeBookFromCharacter(id, bookId))
    if (allCharactersByBook.length === 1) {
      dispatch(_selectCharacterAttributeBookTab())
    }
  })(dispatch)
}

/**
 * @param {number} id
 * @param {String} templateId
 * @return {{ type: 'REMOVE_TEMPLATE_FROM_CHARACTER', id: number, templateId: String }}
 */
export function removeTemplateFromCharacter(id, templateId) {
  return { type: REMOVE_TEMPLATE_FROM_CHARACTER, id, templateId }
}

// NOTE: "load", "loadSingle", "batchLoad" and "removeSingle" are for
// external sync and not for general use.
/**
 * @param {boolean} patching
 * @param {Array<Character>} characters
 * @return {{ type: 'LOAD_CHARACTERS', patching: boolean, characters: Array<Character> }}
 */
export function load(patching, characters) {
  return { type: LOAD_CHARACTERS, patching, characters }
}

// NOTE: "load", "loadSingle", "batchLoad" and "removeSingle" are for
// external sync and not for general use.
/**
 * @param {boolean} patching
 * @param {Character} character
 * @return {{ type: 'LOAD_CHARACTER', patching: boolean, character: Character }}
 */
export function loadSingle(patching, character) {
  return { type: LOAD_CHARACTER, patching, character }
}

// NOTE: "load", "loadSingle", "batchLoad" and "removeSingle" are for
// external sync and not for general use.
/**
 * @param {boolean} patching
 * @param {Array<Character>} characters
 * @return {{ type: 'BATCH_LOAD_CHARACTER', patching: boolean, characters: Array<Character> }}
 */
export function batchLoad(patching, characters) {
  return { type: BATCH_LOAD_CHARACTER, patching, characters }
}

// NOTE: "load", "loadSingle", "batchLoad" and "removeSingle" are for
// external sync and not for general use.
/**
 * @param {boolean} patching
 * @param {Character} character
 * @return {{ type: 'REMOVE_CHARACTER', patching: boolean, character: Character }}
 */
export function removeSingle(patching, character) {
  return { type: REMOVE_CHARACTER, patching, character }
}

/**
 * @param {number} id
 * @return {{ type: 'DUPLICATE_CHARACTER', id: number }}
 */
export function duplicateCharacter(id) {
  return { type: DUPLICATE_CHARACTER, id }
}

/**
 * @param {AttributeType} type
 * @param {String} name
 * @param {boolean} fromLegacyAttribute
 * @return {{ type: 'CREATE_CHARACTER_ATTRIBUTE', attribute: { type: AttributeType, name: String } | { type: AttributeType, name: String, value: AttributeValue | undefined }, fromLegacyAttribute: boolean }}
 */
export function createCharacterAttribute(type, name, fromLegacyAttribute) {
  return {
    type: CREATE_CHARACTER_ATTRIBUTE,
    attribute: {
      type,
      name: escapeBraces(name),
    },
    fromLegacyAttribute,
  }
}

/**
 * @param {'series' | number | Null} bookId
 * @param {number} characterId
 * @param {number | String} attributeId
 * @param {TextAttributeData} value
 * @param {DOMSelection | SlateSelection | Null} selection
 * @return {{ type: 'EDIT_CHARACTER_ATTRIBUTE_VALUE', bookId: 'series' | number | Null, characterId: number, attributeId: number | String, value: TextAttributeData, selection: DOMSelection | SlateSelection | Null }}
 */
export const _editCharacterAttributeValue = (
  bookId,
  characterId,
  attributeId,
  value,
  selection
) => {
  return {
    type: EDIT_CHARACTER_ATTRIBUTE_VALUE,
    bookId,
    characterId,
    attributeId,
    value,
    selection,
  }
}

/**
 * @param {String} attributeName
 * @return {{ type: DELETE_CHARACTER_LEGACY_CUSTOM_ATTRIBUTE, attributeName: String }}
 */
export const _deleteCharacterLegacyCustomAttribute = (attributeName) => {
  return {
    type: DELETE_CHARACTER_LEGACY_CUSTOM_ATTRIBUTE,
    attributeName,
  }
}

/**
 * @param {number} characterId
 * @param {number | String} attributeId
 * @param {TextAttributeData} value
 * @param {DOMSelection | SlateSelection | Null} selection
 * @return {function(Dispatch, GetState): Void}
 */
export const editCharacterAttributeValue =
  (characterId, attributeId, value, selection) => (dispatch, getState) => {
    if (!attributeId) {
      return
    }

    const state = getState()
    const characterAttributesForBook = characterAttributesForCurrentBookSelector(state)
    const selectedBook = selectedCharacterAttributeTabSelector(state)
    const newAttribute = characterAttributesForBook.find((attribute) => {
      return 'id' in attribute && attribute.id === attributeId
    })
    if (newAttribute) {
      if ('bookId' in newAttribute && newAttribute.bookId !== selectedBook) {
        // This is the first time we're editing an attribute for the
        // current book that was previously only defined for the whole
        // series.
        dispatch(
          // NOTE(ed): We add extra values to the action in the root
          // reducer.
          //
          // @ts-ignore
          _editCharacterAttributeValue(selectedBook, characterId, attributeId, value, selection)
        )
      } else {
        dispatch(
          // NOTE(ed): We add extra values to the action in the root
          // reducer.
          //
          // @ts-ignore
          _editCharacterAttributeValue(null, characterId, attributeId, value, selection)
        )
      }
    } else {
      const legacyCustomAttribute =
        typeof attributeId === 'string' && legacyCustomCharacterAttributeByName(state, attributeId)
      if (legacyCustomAttribute) {
        const characterAttributes = characterAttributesForBookSelector(state)
        const nextAttributeId = nextId(characterAttributes)
        batch('Edit Character Attribute', () => {
          dispatch(
            // The root reducer adds `attributeId`.  Calling it this
            // way is an edge case.
            //
            // @ts-ignore
            createCharacterAttribute(legacyCustomAttribute.type, legacyCustomAttribute.name, true)
          )
          dispatch(_deleteCharacterLegacyCustomAttribute(attributeId))
          editCharacterAttributeValue(
            characterId,
            nextAttributeId,
            value,
            selection
          )(dispatch, getState)
        })(dispatch)
      }
    }

    // TODO: handle error state.  There was no legacy attribute.
  }

/**
 * @param {number} characterId
 * @param {String} shortDescription
 * @param {DOMSelection} selection
 * @return {{ type: 'EDIT_CHARACTER_SHORT_DESCRIPTION', characterId: number, value: String, selection: DOMSelection }}
 */
export const editShortDescription = (characterId, shortDescription, selection) => {
  return {
    type: EDIT_CHARACTER_SHORT_DESCRIPTION,
    characterId,
    value: shortDescription,
    selection,
  }
}

/**
 * @param {number} characterId
 * @param {Slate | String} description
 * @param {SlateSelection | Null} selection
 * @return {{ type: 'EDIT_CHARACTER_DESCRIPTION', characterId: number, value: Slate | String, selection: SlateSelection | Null }}
 */
export const editDescription = (characterId, description, selection) => {
  return {
    type: EDIT_CHARACTER_DESCRIPTION,
    characterId,
    value: description,
    selection,
  }
}

/**
 * @param {number} characterId
 * @param {String | Null} categoryId
 * @return {{ type: 'EDIT_CHARACTER_CATEGORY', characterId: number, value: String | Null }}
 */
export const editCategory = (characterId, categoryId) => {
  return {
    type: EDIT_CHARACTER_CATEGORY,
    characterId,
    value: categoryId,
  }
}

/**
 * @param {number} originalPosition
 * @param {number} destination
 * @param {number} characterId
 * @return {{ type: 'REORDER_CHARACTER_TEMPLATES', originalPosition: number, destination: number, id: number }}
 */
export const reorderCharacterTemplateAttribute = (originalPosition, destination, characterId) => {
  return {
    type: REORDER_CHARACTER_TEMPLATES,
    originalPosition,
    destination,
    id: characterId,
  }
}

/**
 * @param {Array<number>} characterIdsInOrder
 * @param {number} characterId
 * @param {number} newPosition
 * @param {String | Null} newCategoryId
 * @param {'up' | 'down'} direction
 * @return {{ type: 'REORDER_CHARACTER_MANUALLY', characterIdsInOrder: Array<number>, characterId: number, newPosition: number, newCategoryId: String | Null, direction: 'up' | 'down' }}
 */
export const _reorderCharacterManually = (
  characterIdsInOrder,
  characterId,
  newPosition,
  newCategoryId,
  direction
) => {
  return {
    type: REORDER_CHARACTER_MANUALLY,
    characterIdsInOrder,
    characterId,
    newPosition,
    newCategoryId,
    direction,
  }
}

/**
 * @param {number} characterId
 * @param {number} newPosition
 * @param {String | Null} newCategoryId
 * @param {'up' | 'down'} direction
 * @return {function(Dispatch, GetState): Void}
 */
export const reorderCharacter =
  (characterId, newPosition, newCategoryId, direction) => (dispatch, getState) => {
    const characterIdsInOrder = sortBy(
      Object.entries(visibleSortedCharactersByCategorySelector(getState(), null, null)),
      ([groupName, _characters]) => groupName
    )
      .flatMap(([_groupName, characters]) => {
        return characters
      })
      .map(({ id }) => {
        return id
      })

    dispatch(
      // The root reducer adds more data.  Calling it this way is an
      // edge case.
      //
      // @ts-ignore
      _reorderCharacterManually(
        characterIdsInOrder,
        characterId,
        newPosition,
        newCategoryId,
        direction
      )
    )
  }
